// src/pages/FundPlanner/components/FundStructurer/FundSetup.jsx
import React, { useEffect, useMemo, useState } from "react";
import { DropdownSelect, DateInput, NumberInput } from "components/InputStyled";
import { adjustToBusinessDay } from "lib/dateUtils";
import { supabase } from "services/supabaseClient";
import { useFundPlanner } from "pages/FundPlanner/context/StructurerContext";
import { Lock } from "lucide-react";
import { SHARING_OPTIONS, normalizeSharing } from "lib/permissions";

function formatDealLabel(d) {
  if (!d) return "(Unknown)";
  const issuer =
    d?.issuers?.legal_name ||
    d?.issuer_name_raw ||
    d?.issuer_name ||
    "(Unknown Issuer)";
  const issue = d?.name || "(Untitled Issue)";
  return `${issuer}, ${issue}`;
}

const linkCls = "underline text-[hsl(var(--primary))] hover:opacity-90";

export default function FundSetup({
  fundType,
  setFundType,
  fundMode: _fundMode,
  setFundMode: _setFundMode,
  settlementDate,
  setSettlementDate,
  totalAmount,
  setTotalAmount,
  totalAmountLocked = false,
  onRunWizard,
  fundName,
  setFundName,
  linkedDealId,
  setLinkedDealId: _setLinkedDealId,
  selectedOrg,
  sharing,
  setSharing,
  canChangeSharing = false,
  isReadOnly = false,
}) {
  // From FundPlannerContext
  const { setTotalAmountFromLink } = useFundPlanner();

  const [availableDeals, setAvailableDeals] = useState([]);

  const isFundLinked = useMemo(() => !!linkedDealId, [linkedDealId]);

  // Linked funds are locked
  const isLocked = isFundLinked || isReadOnly;

  const amountReadOnly = isLocked || totalAmountLocked;

  /**
   * Linked amount sync is meaningful for DS-linked funds that are driven off DS gross.
   * Today: Project + Refunding.
   */
  const canSuggestLinkedAmounts = useMemo(() => {
    if (!isFundLinked) return false;
    return fundType === "project" || fundType === "refunding";
  }, [fundType, isFundLinked]);

  /* Enforce default amount (legacy behavior), do not apply defaults for linked funds */
  useEffect(() => {
    if (isFundLinked) return;
    if (totalAmountLocked) return;
    if (!totalAmount) setTotalAmount(100_000_000);
  }, [isFundLinked, totalAmountLocked, totalAmount, setTotalAmount]);

  /* Normalize settlement to business day */
  useEffect(() => {
    if (!settlementDate) return;
    const adj = adjustToBusinessDay(settlementDate, "following");
    if (adj.format("YYYY-MM-DD") !== settlementDate) {
      setSettlementDate(adj.format("YYYY-MM-DD"));
    }
  }, [settlementDate, setSettlementDate]);

  /* Load ds_deals visible under RLS (only when linked) */
  useEffect(() => {
    let cancelled = false;

    const loadDeals = async () => {
      if (!selectedOrg?.id || !isFundLinked) {
        setAvailableDeals([]);
        return;
      }

      const { data, error } = await supabase
        .from("ds_deals")
        .select(
          `
            id,
            name,
            target_gross,
            issuer_id,
            issuer_name_raw,
            issuers:issuer_id (
              legal_name
            )
          `
        )
        .eq("org_id", selectedOrg.id)
        .order("name", { ascending: true });

      if (cancelled) return;

      if (error) {
        console.error("Error fetching linked deals:", error);
        setAvailableDeals([]);
        return;
      }

      setAvailableDeals(data || []);
    };

    loadDeals();

    return () => {
      cancelled = true;
    };
  }, [selectedOrg?.id, isFundLinked]);

  /**
   * Authoritative DS gross → Total Amount override (linked + locked funds)
   * Net proceeds from ds_deals_outputs is informational only.
   */
  useEffect(() => {
    let cancelled = false;

    const loadLinkedAmounts = async () => {
      if (!canSuggestLinkedAmounts) return;
      if (!linkedDealId) return;

      // 1) Gross from ds_deals.target_gross (authoritative)
      const { data: dealRow, error: dealErr } = await supabase
        .from("ds_deals")
        .select("id,target_gross,delivery_date")
        .eq("id", linkedDealId)
        .maybeSingle();

      if (cancelled) return;

      if (dealErr) {
        console.error("Error fetching linked deal gross:", dealErr);
      } else {
        const gross = Number(dealRow?.target_gross);
        if (Number.isFinite(gross) && gross > 0) {
          // Force override, linked funds are locked and should reflect DS gross in-session
          setTotalAmountFromLink(gross, { force: true });
        }
      }
    };

    loadLinkedAmounts();

    return () => {
      cancelled = true;
    };
  }, [
    canSuggestLinkedAmounts,
    linkedDealId,
    setTotalAmountFromLink,
  ]);

  const lockWrapCls = isLocked ? "opacity-70 pointer-events-none" : "";
  const lockCursorCls = isLocked ? "cursor-not-allowed" : "";

  const linkedDealRow =
    isFundLinked && linkedDealId
      ? availableDeals.find((d) => d.id === linkedDealId) || null
      : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        {isFundLinked ? (
          <div className="flex flex-col min-w-72">
            <label className="text-xs font-medium text-muted mb-1 flex items-center gap-2">
              Linked DebtStudio Issue
              <span className="inline-flex items-center gap-1 text-muted">
                <Lock size={14} />
                <span className="text-xs">Read Only</span>
              </span>
            </label>

            {selectedOrg ? (
              <select
                className="h-9 rounded-md border border-input bg-background text-sm px-3 shadow-sm
                           focus:outline-none focus:ring-2 focus:ring-ring opacity-70 cursor-not-allowed"
                value={linkedDealId || ""}
                disabled
                title="This fund is linked to a DebtStudio Issue. Use Save As to create an unlinked scenario."
              >
                {linkedDealId && (
                  <option value={linkedDealId}>
                    {linkedDealRow
                      ? formatDealLabel(linkedDealRow)
                      : "(Linked Issue)"}
                  </option>
                )}

                {availableDeals
                  .filter((d) => d.id !== linkedDealId)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {formatDealLabel(d)}
                    </option>
                  ))}
              </select>
            ) : (
              <div
                className="h-9 rounded-md border border-input bg-background text-sm px-3 shadow-sm flex items-center opacity-70"
                title="This fund is linked. Organization context is not available in this view."
              >
                (Linked Issue)
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col min-w-72">
            <label className="text-xs font-medium text-muted mb-1">
              Fund Name
            </label>
            <input
              type="text"
              value={fundName || ""}
              onChange={(e) => {
                if (!isLocked) setFundName(e.target.value);
              }}
              className="h-9 rounded-md border border-input bg-background text-sm px-3 shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g., Series 2025 Project Fund"
              disabled={isLocked}
            />
          </div>
        )}
      </div>

      <div className={`flex flex-wrap gap-4 items-start ${lockCursorCls}`}>
        <div className={lockWrapCls}>
          <DropdownSelect
            label="Fund Type"
            value={fundType}
            onChange={(v) => {
              if (isLocked) return;
              setFundType(v);
            }}
            options={[
              { value: "project", label: "Project Fund" },
              { value: "refunding", label: "Refunding Deposit" },
              { value: "capint", label: "Capitalized Interest" },
              { value: "dsf", label: "Debt Service Fund (P&I)" },
              { value: "reserve", label: "Debt Service Reserve" },
            ]}
            className="min-w-56"
          />
        </div>

        <div className={lockWrapCls}>
          <DateInput
            label="Settlement / Issue Date"
            value={settlementDate}
            onChange={(v) => {
              if (isLocked) return;
              setSettlementDate(v);
            }}
            className="w-48"
            disabled={isLocked}
          />
        </div>

        <div className="flex flex-col w-56">
          <div className={amountReadOnly ? "opacity-70" : ""}>
            <NumberInput
              label="Gross Cash Flow Requirement ($)"
              value={totalAmount}
              onChange={(v) => {
                if (amountReadOnly) return;
                setTotalAmount(v);
              }}
              className="w-56"
              disabled={amountReadOnly}
            />
          </div>

          {totalAmountLocked && !isLocked ? (
            <div className="mt-1 text-xs text-muted leading-snug">
              This amount is calculated from the Requirements tab. To change
              it, edit the schedule or{" "}
              {typeof onRunWizard === "function" ? (
                <button
                  type="button"
                  onClick={onRunWizard}
                  className={linkCls}
                >
                  run the Cash Flow Wizard
                </button>
              ) : (
                "run the Cash Flow Wizard"
              )}
              .
            </div>
          ) : null}
        </div>

        <DropdownSelect
          label="Sharing"
          value={normalizeSharing(sharing)}
          onChange={(value) => {
            if (canChangeSharing) setSharing(value);
          }}
          options={SHARING_OPTIONS}
          className="w-56"
          disabled={!canChangeSharing}
        />
      </div>

      {isFundLinked && (
        <div className="text-xs text-muted">
          This fund is linked to a DebtStudio Issue. Inputs are locked. Use Save
          As to create scenario variations, then name the copied fund.
        </div>
      )}
    </div>
  );
}
